import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowPathIcon, ExclamationTriangleIcon, PaperAirplaneIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Button } from "../../../components/forms/Buttons/Button";
import { statusTone } from "../../../components/QuestionList/questionStatus";
import { resubmitQuestion } from "../../../services/reviewService";
import { getApiErrorCode, getCodedErrorMessage } from "../../../utils/apiError";
import { UserContext } from "../../../utils/contexts/UserContext";
import { formatDateTime } from "../../../utils/formatDate";
import { cardClass } from "../../Exams/TestBuilder/ui";
import { questionBooklets } from "./bookletLock";
import CommentField from "./CommentField";
import {
  COMMENT_LIMIT,
  cleanComment,
  latestStatusRecord,
  missingFieldsText,
  recordComments,
  userDisplayName,
} from "./reviewUtils";

const LOOKS = {
  CHANGES_REQUESTED: { icon: PencilIcon, accent: "border-violet-500", title: "question_detail.review.feedback.changes_requested_title" },
  DECLINED: { icon: XMarkIcon, accent: "border-red-500", title: "question_detail.review.feedback.declined_title" },
};

/**
 * For the question's author: why it came back (the reviewer's comment on its return or refusal) and, when the
 * detail's actions.resubmit allows it (and no active BTS booklet prints it), its resubmit with an optional comment.
 * What review still needs (actions.missing, or 400 incomplete) is named. Nothing for anyone else, or for a question
 * under way.
 */
export default function AuthorReviewPanel({ question, history, onChanged, onReload }) {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  const commentRef = useRef(null);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const code = question.status?.code;
  const look = LOOKS[code];
  const own = !!user?.uuid && question.author?.uuid === user.uuid;
  const canResubmit = !!question.actions?.resubmit && !questionBooklets(question).length;
  if (!canResubmit && !(own && look)) return null;

  const record = look ? latestStatusRecord(history, code) : null;
  const comments = recordComments(record);
  const missing = question.actions?.missing || [];
  const Icon = look?.icon || PencilIcon;

  const resubmit = async () => {
    setSending(true);
    setError(null);
    setCommentError("");
    try {
      await resubmitQuestion(question.uuid, { comment: cleanComment(comment) });
      toast.success(t("question_detail.review.resubmitted"));
      setComment("");
      onChanged();
    } catch (err) {
      const errorCode = getApiErrorCode(err);
      const data = err?.response?.data;
      if (Array.isArray(data?.comment)) {
        setCommentError(t("question_detail.review.comment_too_long", { max: COMMENT_LIMIT }));
        commentRef.current?.focus();
      } else if (errorCode === "incomplete" && data?.missing?.length) {
        setError({ message: t("question_detail.review.missing_before_resubmit", { fields: missingFieldsText(t, data.missing) }) });
      } else {
        setError({
          message: getCodedErrorMessage(t, err, t("question_detail.review.resubmit_failed")),
          // Someone else changed its status meanwhile.
          reload: errorCode === "transition_not_allowed",
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section aria-labelledby="author-review-title" className={`${cardClass} overflow-hidden`}>
      <div className={`border-l-4 p-5 sm:p-6 ${look?.accent || "border-violet-500"}`}>
        <div className="flex items-start gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${statusTone(code).badge}`}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 id="author-review-title" className="text-lg font-semibold tracking-tight text-slate-900">
              {t(look?.title || LOOKS.CHANGES_REQUESTED.title)}
            </h3>
            {canResubmit && <p className="mt-0.5 text-sm text-slate-600">{t("question_detail.review.feedback.resubmit_hint")}</p>}
          </div>
        </div>

        {record && (
          <figure className="mt-4 rounded-xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200">
            <figcaption className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{t("question_detail.review.feedback.reviewer_comment")}</span>
              <span>{userDisplayName(t, record.user)}</span>
              <time dateTime={record.created} className="tabular-nums">
                {formatDateTime(record.created, i18n.language)}
              </time>
            </figcaption>
            {comments.length > 0 ? (
              comments.map((text, index) => (
                <blockquote key={index} className="mt-2 whitespace-pre-line break-words text-sm leading-relaxed text-slate-800">
                  {text}
                </blockquote>
              ))
            ) : (
              <p className="mt-2 text-sm italic text-slate-500">{t("question_detail.history.no_comment")}</p>
            )}
          </figure>
        )}

        {canResubmit && (
          <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
            {missing.length > 0 && (
              <p
                id="resubmit-missing"
                className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                {t("question_detail.review.missing_before_resubmit", { fields: missingFieldsText(t, missing) })}
              </p>
            )}
            <CommentField
              ref={commentRef}
              id="resubmitComment"
              label={t("question_detail.review.resubmit_comment_label")}
              placeholder={t("question_detail.review.resubmit_placeholder")}
              value={comment}
              onChange={(value) => {
                setComment(value);
                setCommentError("");
              }}
              error={commentError}
              disabled={sending}
            />
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                text={t("question_detail.transitions.resubmit")}
                Icon={PaperAirplaneIcon}
                color="primary"
                loading={sending}
                disabled={missing.length > 0}
                onClick={resubmit}
                aria-describedby={missing.length > 0 ? "resubmit-missing" : undefined}
                className="max-sm:w-full max-md:h-11"
              />
            </div>
            {error && (
              <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1">{error.message}</span>
                {error.reload && (
                  <Button
                    text={t("question_detail.review.reload")}
                    Icon={ArrowPathIcon}
                    color="secondary-outline"
                    onClick={() => {
                      setError(null);
                      onReload();
                    }}
                    className="!h-9 !px-3 max-md:!h-11"
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
